import { useCallback, useMemo } from 'react';

/**
 * Hook personalizado para Google Tag Manager
 * Permite enviar eventos y datos personalizados a GTM desde componentes React
 */
const useGTM = () => {
  /**
   * Envía un evento a Google Tag Manager
   * @param {string} event - Nombre del evento
   * @param {Object} data - Datos adicionales del evento
   */
  const sendGTMEvent = useCallback((event, data = {}) => {
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event,
        ...data,
      });
    } else {
      // En desarrollo, mostrar en consola
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.log('GTM Event:', { event, ...data });
      }
    }
  }, []);

  /**
   * Envía datos de página vista a GTM
   * @param {string} pagePath - Ruta de la página
   * @param {string} pageTitle - Título de la página
   */
  const sendPageView = useCallback((pagePath, pageTitle) => {
    sendGTMEvent('page_view', {
      page_path: pagePath,
      page_title: pageTitle,
    });
  }, [sendGTMEvent]);

  /**
   * Envía evento de login
   * @param {string} method - Método de login (email, google, etc.)
   * @param {string} userRole - Rol del usuario
   */
  const sendLoginEvent = useCallback((method, userRole) => {
    sendGTMEvent('login', {
      method,
      user_role: userRole,
    });
  }, [sendGTMEvent]);

  /**
   * Envía evento de registro
   * @param {string} method - Método de registro
   * @param {string} userRole - Rol del usuario
   */
  const sendSignUpEvent = useCallback((method, userRole) => {
    sendGTMEvent('sign_up', {
      method,
      user_role: userRole,
    });
  }, [sendGTMEvent]);

  /**
   * Envía evento de compra
   * @param {string} transactionId - ID de la transacción
   * @param {number} value - Valor de la compra
   * @param {string} currency - Moneda
   * @param {Array} items - Items comprados
   */
  const sendPurchaseEvent = useCallback((transactionId, value, currency = 'EUR', items = []) => {
    sendGTMEvent('purchase', {
      transaction_id: transactionId,
      value,
      currency,
      items,
    });
  }, [sendGTMEvent]);

  /**
   * Envía evento de inscripción a jornada
   * @param {string} jornadaId - ID de la jornada
   * @param {string} futbolistaId - ID del futbolista
   * @param {number} precio - Precio de la inscripción
   */
  const sendJornadaInscriptionEvent = useCallback((jornadaId, futbolistaId, precio) => {
    sendGTMEvent('jornada_inscription', {
      jornada_id: jornadaId,
      futbolista_id: futbolistaId,
      value: precio,
      currency: 'EUR',
    });
  }, [sendGTMEvent]);

  /**
   * Envía evento de búsqueda
   * @param {string} searchTerm - Término de búsqueda
   * @param {string} searchCategory - Categoría de búsqueda
   */
  const sendSearchEvent = useCallback((searchTerm, searchCategory = '') => {
    sendGTMEvent('search', {
      search_term: searchTerm,
      search_category: searchCategory,
    });
  }, [sendGTMEvent]);

  /**
   * Envía evento personalizado
   * @param {string} eventName - Nombre del evento
   * @param {Object} eventData - Datos del evento
   */
  const sendCustomEvent = useCallback((eventName, eventData = {}) => {
    sendGTMEvent(eventName, eventData);
  }, [sendGTMEvent]);

  return useMemo(() => ({
    sendGTMEvent,
    sendPageView,
    sendLoginEvent,
    sendSignUpEvent,
    sendPurchaseEvent,
    sendJornadaInscriptionEvent,
    sendSearchEvent,
    sendCustomEvent,
  }), [
    sendGTMEvent,
    sendPageView,
    sendLoginEvent,
    sendSignUpEvent,
    sendPurchaseEvent,
    sendJornadaInscriptionEvent,
    sendSearchEvent,
    sendCustomEvent,
  ]);
};

export default useGTM;
